import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { get, post, patch, del } from '../lib/api';
import TopBar from '../components/layout/TopBar';
import SectionCard from '../components/widgets/SectionCard';
import EmptyState from '../components/widgets/EmptyState';
import KpiCard from '../components/widgets/KpiCard';
import { fmtBRL, fmtDate, todayISO } from '../lib/format';
import { Plus, Trash2, ArrowUpFromLine, AlertCircle, Check, Clock } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const blank = () => ({
  company_id: '', supplier: '', description: '', amount: '',
  due_date: todayISO(), paid_date: '', status: 'PENDENTE', notes: '',
});

const StatusPill = ({ s }) => {
  const map = {
    PENDENTE: { cls: 'pill-warning', icon: Clock, label: 'Pendente' },
    PAGO: { cls: 'pill-success', icon: Check, label: 'Pago' },
    ATRASADO: { cls: 'pill-danger', icon: AlertCircle, label: 'Atrasado' },
  };
  const c = map[s] || map.PENDENTE;
  const I = c.icon;
  return (
    <span className={`pill ${c.cls}`}>
      <I size={11} /> {c.label}
    </span>
  );
};

export default function ContasPagar() {
  const { companies, companyFilter } = useApp();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());

  const load = async () => {
    const cid = companyFilter === 'all' ? null : companyFilter;
    const data = await get('/payables', cid ? { company_id: cid } : {});
    setList(data);
  };
  useEffect(() => { load(); }, [companyFilter]);

  const submit = async () => {
    if (!form.company_id || !form.supplier || !form.description) {
      return toast.error('Preencha empresa, fornecedor e descrição');
    }
    const amount = parseFloat(String(form.amount).replace(',', '.'));
    if (!amount || amount <= 0) return toast.error('Valor inválido');
    try {
      await post('/payables', {
        ...form, amount,
        paid_date: form.paid_date || null,
        notes: form.notes || null,
      });
      toast.success('Conta cadastrada');
      setOpen(false);
      setForm(blank());
      load();
    } catch (e) {
      toast.error('Erro ao cadastrar');
    }
  };

  const togglePaid = async (item) => {
    const newStatus = item.status === 'PAGO' ? 'PENDENTE' : 'PAGO';
    await patch(`/payables/${item.id}`, {
      ...item, status: newStatus, paid_date: newStatus === 'PAGO' ? todayISO() : null,
    });
    toast.success(newStatus === 'PAGO' ? 'Marcado como pago' : 'Reaberto');
    load();
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir?')) return;
    await del(`/payables/${id}`);
    toast.success('Excluído');
    load();
  };

  const totalPendente = list.filter((p) => p.status !== 'PAGO').reduce((s, p) => s + p.amount, 0);
  const totalPago = list.filter((p) => p.status === 'PAGO').reduce((s, p) => s + p.amount, 0);
  const totalAtrasado = list.filter((p) => p.status === 'PENDENTE' && p.due_date < todayISO()).reduce((s, p) => s + p.amount, 0);

  return (
    <div className="fade-in">
      <TopBar
        title="Contas a Pagar"
        subtitle="Obrigações financeiras do grupo"
        action={
          <Button onClick={() => { setForm(blank()); setOpen(true); }} className="btn-gold rounded-md gap-1.5 px-4" data-testid="btn-new-payable">
            <Plus size={15} /> Nova Conta
          </Button>
        }
      />
      <div className="px-8 py-7 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KpiCard label="A Pagar (Pendentes)" value={totalPendente} accent="#F59E0B" icon={Clock} testid="kpi-pendente" />
          <KpiCard label="Pagas no Período" value={totalPago} accent="#10B981" icon={Check} testid="kpi-pago" />
          <KpiCard label="Em Atraso" value={totalAtrasado} accent="#EF4444" icon={AlertCircle} testid="kpi-atrasado" />
        </div>

        <SectionCard padded={false} testid="payables-table">
          {list.length === 0 ? (
            <div className="p-12">
              <EmptyState
                icon={ArrowUpFromLine}
                title="Nenhuma conta a pagar"
                description="Cadastre suas obrigações para controlar vencimentos."
                action={<Button onClick={() => setOpen(true)} className="btn-gold">Cadastrar Conta</Button>}
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full ceap-table">
                <thead>
                  <tr>
                    <th>Vencimento</th>
                    <th>Fornecedor</th>
                    <th>Descrição</th>
                    <th>Empresa</th>
                    <th>Status</th>
                    <th className="text-right">Valor</th>
                    <th className="text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((p) => {
                    const co = companies.find((c) => c.id === p.company_id);
                    const isLate = p.status === 'PENDENTE' && p.due_date < todayISO();
                    return (
                      <tr key={p.id} data-testid={`payable-row-${p.id}`}>
                        <td className={`font-mono ${isLate ? 'red-text font-semibold' : 'text-zinc-300'}`}>{fmtDate(p.due_date)}</td>
                        <td className="text-zinc-100">{p.supplier}</td>
                        <td className="text-zinc-400 text-xs">{p.description}</td>
                        <td className="text-zinc-300 text-xs">{co?.name || '-'}</td>
                        <td><StatusPill s={isLate ? 'ATRASADO' : p.status} /></td>
                        <td className="text-right font-mono text-zinc-50">{fmtBRL(p.amount)}</td>
                        <td className="text-right">
                          <div className="flex justify-end gap-1">
                            <button onClick={() => togglePaid(p)} className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-emerald-400" title="Marcar pago/pendente" data-testid={`btn-toggle-${p.id}`}>
                              <Check size={13} />
                            </button>
                            <button onClick={() => remove(p.id)} className="p-1.5 rounded hover:bg-red-900/30 text-zinc-400 hover:text-red-400" data-testid={`btn-delete-payable-${p.id}`}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-xl">
          <DialogHeader><DialogTitle className="font-display">Nova Conta a Pagar</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Empresa *</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-1"><SelectValue placeholder="Empresa" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Fornecedor *</Label>
              <Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" data-testid="input-supplier" />
            </div>
            <div className="col-span-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Descrição *</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" data-testid="input-desc" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Valor</Label>
              <Input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1 font-mono" placeholder="0,00" data-testid="input-amount-pay" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Vencimento</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1 font-mono" data-testid="input-due-date" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-zinc-400 hover:bg-zinc-900">Cancelar</Button>
            <Button onClick={submit} className="btn-gold" data-testid="btn-save-payable">Cadastrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
